import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { usePoints } from '../hooks/usePoints';
import { getPointArea } from '../api/points';
import { getMe } from '../api/auth';
import { PointCard } from '../components/Dashboard/PointCard';
import { CreatePointButton } from '../components/Dashboard/CreatePointButton';
import { ProfileSection } from '../components/Dashboard/ProfileSection';
import { ZonesSection } from '../components/Dashboard/ZonesSection';
import { BackgroundCanvas } from '../components/BackgroundCanvas';
import { PageHeader } from '../components/PageHeader/PageHeader';
import { Point } from '../types/point';
import { toastError, toastSuccess } from '../utils/toast';
import { AppDispatch, RootState } from '../store';
import { fetchColorCatalog, initiateSubscriptionPayment } from '../store/colorsSlice';
import { trackEvent } from '../utils/analytics';
import '../styles/dashboard.scss';

type MobileTab = 'pixels' | 'zones' | 'profile';

const PAGE_SIZE = 9;

const getDashboardTab = (value: string | null): MobileTab => {
  if (value === 'zones' || value === 'profile') return value;
  return 'pixels';
};

export const DashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const { points, loading, error, fetchPoints, createPoint, deletePoint, updatePoint } =
    usePoints();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams, setSearchParams] = useSearchParams();
  const catalog      = useSelector((s: RootState) => s.colors.catalog);
  const subscription = useSelector((s: RootState) => s.colors.subscription);

  const [pointAreas, setPointAreas] = useState<Record<number, Point[]>>({});
  const [maxPoints, setMaxPoints]   = useState<number>(3);
  const [search, setSearch]         = useState('');
  const [page, setPage]             = useState(0);
  const [buyingSub, setBuyingSub]   = useState(false);
  const [mobileTab, setMobileTab]   = useState<MobileTab>(() => getDashboardTab(searchParams.get('tab')));

  useEffect(() => {
    fetchPoints();
    getMe().then(res => setMaxPoints(res.data.max_points)).catch(() => {});
    dispatch(fetchColorCatalog());
  }, [dispatch]); // eslint-disable-line

  useEffect(() => {
    setMobileTab(getDashboardTab(searchParams.get('tab')));
  }, [searchParams]);

  // Handle return from any payment
  useEffect(() => {
    const result = searchParams.get('payment');
    if (!result) return;
    if (result === 'zone_success') {
      setMobileTab('zones');
      trackEvent('payment_success', { kind: 'zone' });
      toastSuccess(t('zone.paymentSuccess'));
    } else if (result === 'color_success' || result === 'success') {
      dispatch(fetchColorCatalog());
      trackEvent('payment_success', { kind: result === 'success' ? 'subscription' : 'color' });
      toastSuccess(t('shop.paymentSuccess'));
    } else if (result === 'fail') {
      trackEvent('payment_fail');
      toastError(t('shop.paymentFail'));
    }
    const next = new URLSearchParams(searchParams);
    next.delete('payment');
    setSearchParams(next, { replace: true });
  }, [dispatch, searchParams, setSearchParams, t]);

  useEffect(() => { setPage(0); }, [search]);

  const q = search.trim().toLowerCase();
  const filteredPoints = q
    ? points.filter(p =>
        p.name.toLowerCase().includes(q) ||
        String(p.x).includes(q) ||
        String(p.y).includes(q) ||
        `${p.x},${p.y}`.includes(q.replace(/\s/g, '')),
      )
    : points;
  const totalPages  = Math.ceil(filteredPoints.length / PAGE_SIZE);
  const pagedPoints = filteredPoints.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const pagedPointIds = pagedPoints.map(p => p.id).join(',');
  useEffect(() => {
    if (pagedPoints.length === 0) return;
    for (const p of pagedPoints) {
      if (pointAreas[p.id]) continue;
      getPointArea(p.id)
        .then(res => setPointAreas(prev => ({ ...prev, [p.id]: res.data.points ?? [] })))
        .catch(() => {});
    }
  }, [pagedPointIds]); // eslint-disable-line

  const handleBuySubscription = async () => {
    setBuyingSub(true);
    trackEvent('subscription_purchase_initiate', {
      from: 'dashboard',
      already_active: subscription.active,
    });
    try {
      const paymentUrl = await dispatch(initiateSubscriptionPayment()).unwrap();
      window.location.href = paymentUrl;
    } catch (e: any) {
      toastError(e?.message || t('shop.errSub'));
      setBuyingSub(false);
    }
  };

  const handlePlay   = (id: number) => {
    trackEvent('point_play', { point_id: id, from: 'dashboard' });
    navigate(`/game/${id}`);
  };
  const handleCreate = async (name: string) => {
    try {
      const point = await createPoint(name);
      trackEvent('point_create', { point_id: point.id, from: 'dashboard' });
      navigate(`/game/${point.id}`);
    } catch (e: any) { toastError(typeof e === 'string' ? e : (e?.message || t('dashboard.errCreate'))); }
  };
  const handleDelete = async (id: number) => {
    try {
      await deletePoint(id);
      trackEvent('point_delete', { point_id: id, from: 'dashboard' });
    } catch (e: any) { toastError(typeof e === 'string' ? e : (e?.message || t('dashboard.errDelete'))); }
  };
  const handleRename = async (id: number, newName: string) => {
    try {
      await updatePoint(id, newName);
      trackEvent('point_rename', { point_id: id, from: 'dashboard' });
    } catch (e: any) { toastError(typeof e === 'string' ? e : (e?.message || t('dashboard.errRename'))); }
  };
  const handleColorChange = async (id: number, color: string) => {
    try {
      await updatePoint(id, '', color);
      trackEvent('point_color_change', { point_id: id, color, from: 'dashboard' });
    } catch (e: any) { toastError(typeof e === 'string' ? e : (e?.message || t('dashboard.errColor'))); }
  };

  const purchasedColors = catalog.filter(c => c.purchased);

  if (loading) {
    return (
      <div className="dashboard">
        <BackgroundCanvas opacity={0.15} />
        <div className="dashboard__loading">{t('dashboard.loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard">
        <BackgroundCanvas opacity={0.15} />
        <div className="dashboard__error">{t('common.error', { msg: error })}</div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <BackgroundCanvas opacity={0.15} />

      <PageHeader title={t('dashboard.title')} subtitle={t('dashboard.subtitle')} current="dashboard" />

      <div style={{ textAlign: 'center', fontSize: '0.62rem', opacity: 0.55, margin: '0.25rem 0 0.5rem' }}>
        {t('common.ofertaNotice')}{' '}
        <Link to="/about?tab=oferta" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--px-accent)', textDecoration: 'underline' }}>
          {t('common.ofertaLink')}
        </Link>
      </div>

      {/* ── Mobile tab bar (hidden on desktop via CSS) ──────────── */}
      <div className="dashboard__mobile-tabs">
        <button
          className={`dashboard__mobile-tab${mobileTab === 'pixels'  ? ' dashboard__mobile-tab--active' : ''}`}
          onClick={() => setMobileTab('pixels')}
        >
          {t('dashboard.tabPixels')}
        </button>
        <button
          className={`dashboard__mobile-tab${mobileTab === 'zones'   ? ' dashboard__mobile-tab--active' : ''}`}
          onClick={() => setMobileTab('zones')}
        >
          {t('dashboard.tabZones')}
        </button>
        <button
          className={`dashboard__mobile-tab${mobileTab === 'profile' ? ' dashboard__mobile-tab--active' : ''}`}
          onClick={() => setMobileTab('profile')}
        >
          {t('dashboard.tabProfile')}
        </button>
      </div>

      {/* ── Main grid ────────────────────────────────────────────── */}
      <div className="dashboard__layout">

        {/* LEFT: pixels + subscription ─────────────────────────── */}
        <div className={`dashboard__main-col${mobileTab !== 'pixels' ? ' is-mobile-hidden' : ''}`}>

          {/* Stats */}
          <div className="dashboard__stats">
            <div className="stat-chip">
              {t('dashboard.pointsStats')} <span className="stat-chip__value">{points.length} / {maxPoints}</span>
            </div>
            {points.length > 0 && (
              <div className="stat-chip">
                {t('dashboard.lastActive')}{' '}
                <span className="stat-chip__value">
                  {new Date(points[points.length - 1]?.updated_at).toLocaleDateString()}
                </span>
              </div>
            )}
          </div>

          {/* Expanded subscription panel */}
          <div className="dashboard__sub-panel">
            <div className="dashboard__sub-panel__header">
              <span className="dashboard__sub-panel__title">{t('shop.subscriptionTitle')}</span>
              {subscription.active && subscription.expires_at ? (
                <span className="dashboard__sub-active">
                  {t('dashboard.subscriptionActiveUntil', {
                    date: new Date(subscription.expires_at).toLocaleDateString(),
                  })}
                </span>
              ) : (
                <span className="dashboard__sub-inactive">{t('dashboard.subscriptionInactive')}</span>
              )}
            </div>

            <p className="dashboard__sub-panel__desc">{t('shop.subscriptionDesc')}</p>

            <ul className="dashboard__sub-panel__perks">
              <li>{t('shop.perk1')}</li>
              <li>{t('shop.perk2')}</li>
              <li>{t('shop.perk3')}</li>
            </ul>

            <div className="dashboard__sub-panel__footer">
              <span className="dashboard__sub-panel__price">{t('shop.subPrice')}</span>
              <button
                className="dashboard__sub-panel__buy-btn"
                onClick={handleBuySubscription}
                disabled={buyingSub}
              >
                {buyingSub
                  ? t('common.loading')
                  : subscription.active
                    ? t('shop.extendBtn')
                    : t('shop.subscribeBtn')}
              </button>
            </div>
            <p style={{ fontSize: '0.62rem', opacity: 0.55, margin: '0.25rem 0 0', textAlign: 'right' }}>
              {t('common.ofertaConsent')}{' '}
              <Link to="/about?tab=oferta" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--px-accent)', textDecoration: 'underline' }}>
                {t('common.ofertaLink')}
              </Link>
            </p>

            {purchasedColors.length > 0 && (
              <div className="dashboard__colors-owned">
                <span className="dashboard__colors-label">{t('dashboard.ownedColorsLabel')}</span>
                <div className="dashboard__colors-swatches">
                  {purchasedColors.map(c => (
                    <span
                      key={c.color}
                      className="dashboard__color-swatch"
                      style={{ backgroundColor: c.color }}
                      title={c.color}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Pixel grid */}
          {points.length === 0 ? (
            <div className="dashboard__empty">
              <div className="dashboard__empty-icon">[·]</div>
              <p className="dashboard__empty-text">
                {t('dashboard.emptyLine1')}<br />{t('dashboard.emptyLine2')}
              </p>
              <CreatePointButton onCreate={handleCreate} disabled={points.length >= maxPoints} />
            </div>
          ) : (
            <>
              <div className="dashboard__search">
                <input
                  className="dashboard__search-input"
                  type="text"
                  placeholder={t('dashboard.searchPlaceholder')}
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
                {search && (
                  <button className="dashboard__search-clear" onClick={() => setSearch('')}>✕</button>
                )}
              </div>

              {filteredPoints.length === 0 ? (
                <div className="dashboard__empty">
                  <p className="dashboard__empty-text">{t('dashboard.noResults', { query: search.toUpperCase() })}</p>
                </div>
              ) : (
                <>
                  <div className="points-grid">
                    {pagedPoints.map((point, index) => (
                      <PointCard
                        key={point.id}
                        point={point}
                        areaPoints={pointAreas[point.id] ?? []}
                        onPlay={handlePlay}
                        onDelete={handleDelete}
                        onRename={handleRename}
                        onColorChange={handleColorChange}
                        style={{ animationDelay: `${index * 0.05}s` }}
                      />
                    ))}
                  </div>

                  {totalPages > 1 && (
                    <div className="dashboard__pagination">
                      <button
                        className="dashboard__page-btn"
                        onClick={() => setPage(p => Math.max(0, p - 1))}
                        disabled={page === 0}
                      >
                        {t('dashboard.prev')}
                      </button>
                      <span className="dashboard__page-info">
                        {page + 1} / {totalPages}
                      </span>
                      <button
                        className="dashboard__page-btn"
                        onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                        disabled={page >= totalPages - 1}
                      >
                        {t('dashboard.next')}
                      </button>
                    </div>
                  )}
                </>
              )}

              <CreatePointButton onCreate={handleCreate} disabled={points.length >= maxPoints} />
            </>
          )}
        </div>

        {/* ZONES (left column, below pixels) ──────────────────── */}
        <div className={`dashboard__zones-col${mobileTab !== 'zones' ? ' is-mobile-hidden' : ''}`}>
          <h2 className="dashboard__section-title">{t('myZones.title')}</h2>
          <ZonesSection />
        </div>

        {/* SIDEBAR: profile ────────────────────────────────────── */}
        <div className={`dashboard__sidebar${mobileTab !== 'profile' ? ' is-mobile-hidden' : ''}`}>
          <h2 className="dashboard__section-title">{t('profile.heading')}</h2>
          <ProfileSection />
        </div>

      </div>
    </div>
  );
};
